import { CartMode } from "@/types";
import type { ContactLensesData, WidgetConfig } from "@/types/config.types";

/**
 * Determines which eye is missing from the ContactLensesData
 * @param contactLensesData - The contact lenses data containing OD and/or OS
 * @returns Translation key for the missing eye or null if both or neither are present
 */
export const getMissingEyeKey = (
  contactLensesData?: ContactLensesData,
): string | null => {
  if (!contactLensesData) {
    return null;
  }

  const hasOD = !!contactLensesData.OD;
  const hasOS = !!contactLensesData.OS;

  // If only OD is present, OS (left eye) is missing
  if (hasOD && !hasOS) {
    return "left";
  }

  // If only OS is present, OD (right eye) is missing
  if (hasOS && !hasOD) {
    return "right";
  }

  // Both present or both missing
  return null;
};

/**
 * Formats a title by replacing ###MISSING_EYE### placeholder with the translated eye label
 * @param title - The title string containing the placeholder
 * @param contactLensesData - The contact lenses data to determine which eye is missing
 * @param t - Translation function from useTranslation hook
 * @returns Formatted title with the missing eye label
 */
export const formatTitle = (
  title: string,
  contactLensesData?: ContactLensesData,
  t?: (key: string) => string,
): string => {
  const missingEyeKey = getMissingEyeKey(contactLensesData);

  if (!missingEyeKey || !t) {
    // If no missing eye or no translation function, remove the placeholder
    return title.replace("###MISSING_EYE###", "");
  }

  // Replace placeholder with translated eye label
  return title.replace("###MISSING_EYE###", t(missingEyeKey));
};

/**
 * Formats a date string into mm/dd/yyyy format
 * @param dateString - The date string to format
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${month}/${day}/${year}`;
};

/**
 * formats the config to send to e-commerce
 * @param config the widgetconfig
 * @returns the cart mode to sent to e-commerce
 */

export const formatCartMode = (config: WidgetConfig | null): CartMode => {
  return {
    orderId: config?.orderId,
    OD: config?.contactLensesData?.OD?.product?.orderItemId
      ? {
          orderProductId: config.contactLensesData.OD.product.orderItemId,
        }
      : undefined,
    OS: config?.contactLensesData?.OS?.product?.orderItemId
      ? {
          orderProductId: config.contactLensesData.OS.product.orderItemId,
        }
      : undefined,
  };
};
